import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'
import useAgeOptions from 'hooks/Search/useAgeOptions'
import { take } from 'lib/array/arrayUtils'
import { getChildrenAgesLimits } from 'lib/offer/occupancyUtils'
import Group from 'components/utils/Group'
import Select from 'components/Common/Form/Input/Select'
import DateOfBirthInput from 'components/Common/Form/Input/DateOfBirthInput'
import BodyText from 'LuxKit/Typography/BodyText'

const AgeSelect = styled(Select)`
  border-radius: ${props => props.theme.borderRadius.S};
  min-width: ${rem(120)};
`

interface Props extends Omit<React.ComponentProps<typeof Select>, 'onChange' | 'defaultValue'> {
  childId: number
  onChange: (childId: number, age: number, birthDate?: string) => void
  defaultValue?: number
  capacities: Array<App.RoomCapacity> | undefined
  occupancy: App.Occupants
  minInfantAge?: number
  maxInfantAge: number
  maxChildAge: number
  maxTeenagerAge: number
  className?: string
  childrenRequireDateOfBirth?: boolean
  departureDate?: string
  childrenAgeLabel: string
  showChildrenSelectLabel?: boolean
}

function OccupancyChildAgeSelect(props: Props) {
  const { childId, capacities, occupancy, maxChildAge, maxInfantAge, minInfantAge, maxTeenagerAge, onChange, defaultValue, className, childrenRequireDateOfBirth, departureDate, childrenAgeLabel, showChildrenSelectLabel, ...rest } = props
  const [value, setValue] = useState(defaultValue)
  const [birthDateValue, setBirthDateValue] = useState<string | undefined>(occupancy.childrenBirthDate?.[childId])
  const ageOptions = useAgeOptions()

  const ageLimits = useMemo(() => (
    getChildrenAgesLimits(capacities, occupancy, childId, maxInfantAge, maxChildAge, maxTeenagerAge)
  ), [capacities, occupancy, childId, maxChildAge, maxInfantAge, maxTeenagerAge])

  const options = useMemo(() => take(ageOptions, ageLimits.max - ageLimits.min + 1, ageLimits.min), [ageOptions, ageLimits])

  // disable invalid birth dates
  const { minDate, maxDate, refDate } = useMemo(() => {
    if (!childrenRequireDateOfBirth || !departureDate) return {}

    const minAdultAge = Math.max(maxTeenagerAge, maxChildAge) + 1

    const minDate = moment(departureDate).subtract(minAdultAge, 'years').add(1, 'days')
    const maxDate = moment(departureDate).subtract(minInfantAge || 0.5, 'years')

    return {
      minDate: minDate.toDate(),
      maxDate: maxDate.toDate(),
      refDate: new Date(departureDate),
    }
  }, [childrenRequireDateOfBirth, departureDate, minInfantAge, maxChildAge, maxTeenagerAge])

  const onSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = parseInt(e.currentTarget.value, 10)
    setValue(selectedValue)

    // reset birth date if age is greater than maxInfantAge
    if (childrenRequireDateOfBirth) {
      setBirthDateValue(undefined)
      onChange(childId, selectedValue, undefined)
      return
    }

    onChange(childId, selectedValue, birthDateValue)
  }, [childId, onChange, birthDateValue, childrenRequireDateOfBirth])

  const onChangeBirthDate = useCallback((date?: string, isoDate?: string) => {
    if (
      childrenRequireDateOfBirth &&
      isoDate
    ) {
      const age = moment(departureDate).diff(isoDate, 'years')
      setBirthDateValue(isoDate)
      onChange(childId, age, isoDate)
    }
  }, [childId, onChange, childrenRequireDateOfBirth, departureDate])

  return (
    <Group
      direction="horizontal"
      tabletDirection="vertical"
      horizontalAlign="space-between"
      gap={12}
      className={className}
      verticalAlign="space-between"
      style={{ position: 'relative' }}
    >
      <div>
        <BodyText variant="medium">Child {childId + 1}</BodyText>
        {showChildrenSelectLabel && <BodyText variant="medium" colour="neutral-three">{childrenAgeLabel}</BodyText>}
      </div>
      {!childrenRequireDateOfBirth && <AgeSelect
        {...rest}
        onChange={onSelectChange}
        value={value}
        noValidationMessage
        noValidationSpacing
        requiredErrorMessage="Please select child's age"
        placeholder="Select"
      >
        {options.map(age =>
          <option key={age.value} value={age.value}>{age.label}</option>,
        )}
      </AgeSelect>}

      {childrenRequireDateOfBirth && <DateOfBirthInput
        key={`birth-date-${value}`}
        max={maxDate}
        min={minDate}
        defaultValue={birthDateValue}
        onChange={onChangeBirthDate}
        refDate={refDate}
        travellerType="child"
        required
      />}
    </Group>
  )
}

export default OccupancyChildAgeSelect
